import { useState } from 'react'

import { useReleaseHandoffPoint } from '@ctcells/api'
import type { ModuleSnapshot } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import { AddModal } from '../atoms/AddModal.tsx'
import { Button } from '../atoms/Button.tsx'
import { Checkbox } from '../atoms/Checkbox.tsx'
import { Field } from '../atoms/Field.tsx'
import { useSubmitValidation } from '../atoms/useSubmitValidation.ts'
import { TextInput } from '../atoms/TextInput.tsx'
import { ValueRows } from '../atoms/ValueRows.tsx'
import type { ActionGate, ScreenAccess } from '../screens/access.ts'
import { FormSection, ModalFooter } from '../screens/formSections.tsx'
import { historyTargetText } from '../screens/format.ts'
import { moduleWriteGate } from '../screens/modules/moduleInitializeGate.ts'
import { handoffPointIds, resolveHandoffPoint } from './handoffPoint.ts'

export function HandoffReleaseFix({
  access,
  busy,
  module,
  operations,
  pointId: item,
  onRun,
}: {
  access: ScreenAccess
  busy: boolean
  module: ModuleSnapshot | undefined
  operations: readonly string[] | null
  pointId: string | null
  onRun: (send: () => Promise<unknown>) => void
}) {
  const release = useReleaseHandoffPoint()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const validation = useSubmitValidation((value: string) => ({
    reason: value.trim() === '' ? t('validation.reasonRequired') : undefined,
  }))
  const reasonField = validation.field('reason')
  const [checked, setChecked] = useState(false)
  const pointId = resolveHandoffPoint(item, handoffPointIds(module?.handoff_occupancy))
  const gate: ActionGate =
    module === undefined
      ? { disabled: true, disabledReason: t('pfFix.moduleUnknown') }
      : pointId === null || pointId.trim() === ''
        ? { disabled: true, disabledReason: t('pfFix.handoffPointMissing') }
        : module.stale
          ? { disabled: true, disabledReason: t('reason.streamStale') }
          : moduleWriteGate({
              access,
              operations,
              operationIds: ['releaseModuleHandoffPointControl'],
              controlOwner: module.control_owner,
              sending: busy || release.isPending,
            })
  const submitGate: ActionGate = gate.disabled
    ? gate
    : !checked
      ? { disabled: true, disabledReason: t('reason.physicalCheckRequired') }
      : { disabled: false }

  const close = () => {
    setOpen(false)
    setReason('')
    validation.reset()
    setChecked(false)
  }

  return (
    <>
      {/*
        🔴 꺼졌으면 까닭을 창 안 글자로(eric 2026-10-29 「제어락이 있어도 저게 활성화가 안 되는데..」) — title 만으로는
           왜 안 눌리는지 모른다. 옆 [적재 완료](`StartPreflightActions.tsx` `pfFix.loadMissing`)와 같은 꼴 · 글은 `gate.disabledReason` 그대로
      */}
      {/* 감싸개는 늘 같다 — 켜고 꺼질 때 단추가 다시 만들어지면 초점을 잃는다 */}
      <span className="pffix">
        <Button onClick={() => setOpen(true)} size="pill" {...gate}>
          {t('pfFix.prepare')}
        </Button>
        {gate.disabled ? <span className="small muted">{gate.disabledReason}</span> : null}
      </span>
      {open ? (
        <AddModal size="md" onClose={close} title={t('pfFix.prepare')}>
          <FormSection>
            <ValueRows
              rows={[
                {
                  key: 'module',
                  label: t('column.module'),
                  value: historyTargetText(module?.module_id ?? ''),
                },
                { key: 'point', label: t('panel.handoffPoint'), value: pointId },
              ]}
            />
            <Field {...reasonField.fieldProps} label={t('field.reason')} required>
              <TextInput
                {...reasonField.inputProps}
                onChange={(next) => {
                  setReason(next)
                  validation.change('reason', next)
                }}
                value={reason}
                wide
              />
            </Field>
            <Checkbox
              checked={checked}
              label={t('pfFix.handoffPhysicalCheck')}
              onChange={setChecked}
            />
          </FormSection>
          <ModalFooter>
            <Button onClick={close} size="pill">
              {t('action.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (submitGate.disabled || module === undefined || pointId === null) return
                if (
                  !validation.validate(
                    reason,
                    document.getElementById(reasonField.inputProps.id)?.parentElement ?? null,
                  )
                )
                  return
                const said = reason.trim()
                close()
                onRun(() =>
                  release.mutateAsync({ moduleId: module.module_id, pointId, reason: said }),
                )
              }}
              size="pill"
              variant="primary"
              {...submitGate}
            >
              {t('pfFix.prepare')}
            </Button>
          </ModalFooter>
        </AddModal>
      ) : null}
    </>
  )
}
